/**
 * ZicassoLeadModal — Modal centrado con tabs oficiales de Zicasso:
 *   DETAILS · MESSAGES · DOCUMENTS · SALES · FOLLOW-UP PLAN
 *
 * Incluye composer de mensajes con templates + sugerencia inteligente
 * de próxima acción (según timeline oficial + destino del lead).
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import axios from "axios";
import { RefreshCw, AlertTriangle, Calendar, MapPin, DollarSign, Users, Phone,
  Send, Mail, FileText, Award, ListChecks, Sparkles, Clock, User, Copy,
  BookOpen, MessageSquare, Check, X, Lightbulb, ScrollText,
} from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sofi_token") || ""}` });

const STAGE_COLORS = {
  "": "bg-blue-50 text-blue-700 border-blue-200",
  FIRST_MESSAGE_SENT: "bg-amber-50 text-amber-700 border-amber-200",
  CONSULTATION: "bg-violet-50 text-violet-700 border-violet-200",
  ITINERARY_IN_REVIEW: "bg-cyan-50 text-cyan-700 border-cyan-200",
  ITINERARY_SENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PROPOSAL_SENT: "bg-teal-50 text-teal-700 border-teal-200",
};
const STAGE_LABELS = {
  "": "Nuevo",
  FIRST_MESSAGE_SENT: "1er mensaje",
  CONSULTATION: "Consultation",
  ITINERARY_IN_REVIEW: "Itinerario en revisión",
  ITINERARY_SENT: "Itinerario enviado",
  PROPOSAL_SENT: "Propuesta enviada",
};

const initials = (n = "") => n.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const relativeDate = (iso) => {
  if (!iso || iso.startsWith("0001")) return "—";
  try { return format(new Date(iso), "d MMM yyyy", { locale: es }); } catch { return "—"; }
};

// Sanitizar HTML de Zicasso (strip scripts/styles/on*) para render seguro
const sanitizeHtml = (html = "") => {
  if (!html) return "";
  // Removemos scripts, styles y atributos on*
  return html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "");
};

// HTML → texto plano para preview de templates (Zicasso guarda HTML)
const htmlToText = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText || "";
};

function InfoBadge({ icon: Icon, label, value, color = "" }) {
  return (
    <div className={`flex flex-col rounded-md border p-3 ${color}`}>
      <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

function DetailsTab({ lead }) {
  const traveler = (lead.raw_trip_match?.travelers || [])[0] || {};
  const rawPrefix = (lead.traveler_phone_prefix || "").toString();
  const prefix = rawPrefix.startsWith("+") ? rawPrefix : rawPrefix ? `+${rawPrefix}` : "";
  const phone = `${prefix} ${lead.traveler_phone_number || ""}`.trim();
  const description = lead.cached_trip_request?.description ||
                      lead.cached_trip_request?.notes ||
                      "";
  // Emails extraídos del userInfo del traveler
  const emails = (traveler.userInfo || []).map((u) => u.email).filter(Boolean);
  const primaryEmail = lead.traveler_email || emails[0] || "";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <InfoBadge icon={MapPin} label="Destino" value={(lead.countries || []).join(", ")} />
        <InfoBadge icon={Calendar} label="Salida" value={relativeDate(lead.departure_date)} />
        <InfoBadge icon={Calendar} label="Vuelta" value={relativeDate(lead.return_date)} />
        <InfoBadge icon={Users} label="Pax" value={`${lead.adults}A ${lead.children ? `${lead.children}N` : ""} · ${lead.total_nights}n`} />
        <InfoBadge icon={DollarSign} label="Budget" value={lead.budget_usd ? `$${(lead.budget_usd || 0).toLocaleString()}` : "—"} color="text-emerald-800" />
        <InfoBadge icon={Phone} label="Teléfono" value={phone || "—"} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <InfoBadge icon={Mail} label="Email" value={primaryEmail || "—"} />
        <InfoBadge icon={Award} label="Repeat traveler" value={lead.repeat_traveler ? "Sí (cliente recurrente)" : "No"} />
        <InfoBadge icon={MapPin} label="Zona horaria" value={lead.traveler_timezone || "—"} />
        <InfoBadge icon={User} label="País de residencia" value={lead.traveler_country || "—"} />
      </div>

      {description && (
        <Card>
          <CardContent className="p-4">
            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" /> Descripción del cliente
            </h3>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">
              {description}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Nota interna SOFI */}
      <SofiNoteEditor lead={lead} />
    </div>
  );
}

function SofiNoteEditor({ lead }) {
  const qc = useQueryClient();
  const [text, setText] = useState(lead.sofi_notes || "");
  const mut = useMutation({
    mutationFn: (t) => axios.post(`${API}/api/zicasso/leads/${lead.id}/note`,
      { text: t }, { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Nota guardada");
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
    },
  });
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="mb-2 text-sm font-semibold">Nota interna (SOFI only)</h3>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Nota que solo se ve en SOFI (no se envía a Zicasso)..."
          rows={3}
          data-testid="sofi-note-textarea"
        />
        <Button
          size="sm"
          className="mt-2"
          onClick={() => mut.mutate(text)}
          disabled={mut.isPending}
          data-testid="save-sofi-note-button"
        >
          <Check className="mr-1 h-3 w-3" /> Guardar nota
        </Button>
      </CardContent>
    </Card>
  );
}

function MessagesTab({ lead }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);

  const templates = useQuery({
    queryKey: ["zicasso-templates"],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/templates`, { headers: authHeaders() })).data,
    staleTime: 5 * 60_000,
  });

  const sendMut = useMutation({
    mutationFn: () => axios.post(`${API}/api/zicasso/leads/${lead.id}/send-message`,
      { body, subject: subject || null }, { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Mensaje enviado a Zicasso");
      setBody("");
      setSubject("");
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error enviando mensaje"),
  });

  const refreshMut = useMutation({
    mutationFn: () => axios.get(`${API}/api/zicasso/leads/${lead.id}/messages?refresh=true`,
      { headers: authHeaders() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
      toast.success("Conversación actualizada");
    },
  });

  const applyTemplate = (tpl) => {
    setBody(htmlToText(tpl.body));
    setSubject(tpl.title || "");
    setShowTemplates(false);
    toast.success(`Template aplicado: ${tpl.title}`);
  };

  return (
    <div className="flex h-[560px] flex-col gap-3">
      {/* Historial */}
      <div className="flex-1 overflow-y-auto rounded-md border bg-slate-50 p-3" data-testid="messages-history">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Historial ({lead.messages_count || 0})
          </span>
          <Button size="sm" variant="ghost" onClick={() => refreshMut.mutate()} disabled={refreshMut.isPending}>
            <RefreshCw className={`h-3 w-3 ${refreshMut.isPending ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {(lead.messages || []).length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Sin mensajes intercambiados aún.</p>
        ) : (
          <div className="space-y-2">
            {(lead.messages || []).map((m) => (
              <div key={m.id} className="rounded-md border bg-card p-3 text-sm" data-testid={`message-${m.id}`}>
                <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span className="font-medium">{m.sender_name || m.sender_type || "—"}</span>
                  <span>{relativeDate(m.sent_at)}</span>
                </div>
                {m.subject && <div className="mb-1 text-xs font-semibold text-slate-900">{m.subject}</div>}
                <div
                  className="prose prose-sm max-w-none text-xs leading-relaxed
                             [&_p]:my-1 [&_a]:text-blue-600 [&_a]:underline [&_ul]:my-1 [&_ol]:my-1
                             [&_ul]:pl-4 [&_ol]:pl-4 [&_li]:my-0.5 [&_strong]:font-semibold
                             [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(m.body || "") }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Nuevo mensaje</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowTemplates(!showTemplates)}
            data-testid="toggle-templates-button"
          >
            <BookOpen className="mr-1 h-3 w-3" />
            Templates ({templates.data?.length || 0})
          </Button>
        </div>

        {showTemplates && (
          <div className="max-h-[180px] space-y-1 overflow-y-auto rounded border bg-slate-50 p-2" data-testid="templates-list">
            {(templates.data || []).map((t) => (
              <button
                key={t.id}
                onClick={() => applyTemplate(t)}
                className="flex w-full items-start justify-between rounded p-2 text-left text-xs hover:bg-slate-200"
                data-testid={`template-${t.id}`}
              >
                <div>
                  <div className="font-semibold">{t.title}</div>
                  <div className="mt-0.5 line-clamp-2 text-[10px] text-muted-foreground">
                    {htmlToText(t.body).substring(0, 100)}...
                  </div>
                </div>
                <Copy className="ml-2 h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}

        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject (opcional)"
          data-testid="message-subject-input"
        />
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Escribe el mensaje aquí..."
          rows={5}
          data-testid="message-body-textarea"
        />
        <div className="flex items-center justify-end gap-2">
          {body && (
            <Button size="sm" variant="ghost" onClick={() => { setBody(""); setSubject(""); }} data-testid="clear-composer-button">
              <X className="mr-1 h-3 w-3" /> Limpiar
            </Button>
          )}
          <Button
            onClick={() => sendMut.mutate()}
            disabled={sendMut.isPending || !body.trim()}
            data-testid="send-message-button"
          >
            <Send className={`mr-2 h-3 w-3 ${sendMut.isPending ? "animate-pulse" : ""}`} />
            {sendMut.isPending ? "Enviando..." : "Enviar a Zicasso"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DocumentsTab({ lead }) {
  const docs = lead.documents || [];
  return (
    <div>
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <FileText className="h-4 w-4" /> Documentos ({docs.length})
      </h3>
      {docs.length === 0 ? (
        <p className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground">
          Sin documentos en Zicasso para este lead.
        </p>
      ) : (
        <div className="space-y-2">
          {docs.map((d, i) => (
            <Card key={d.id || i}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{d.name || d.title || "Documento"}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {d.type || d.documentType} · {relativeDate(d.createdAt)}
                    </div>
                  </div>
                  {d.url && (
                    <Button size="sm" variant="outline" asChild>
                      <a href={d.url} target="_blank" rel="noopener noreferrer">Abrir</a>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SalesTab({ lead }) {
  const sr = lead.sales_recordings || [];
  const rv = lead.reviews || [];
  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <DollarSign className="h-4 w-4" /> Sales recordings ({sr.length})
        </h3>
        {sr.length === 0 ? (
          <p className="rounded border border-dashed p-4 text-center text-sm text-muted-foreground">
            Sin sales recordings. Se generan cuando el trip pasa a SOLD.
          </p>
        ) : (
          <pre className="max-h-[300px] overflow-auto rounded bg-slate-50 p-2 text-[10px]">
            {JSON.stringify(sr, null, 2)}
          </pre>
        )}
      </div>
      <div>
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <Award className="h-4 w-4" /> Reviews del traveler ({rv.length})
        </h3>
        {rv.length === 0 ? (
          <p className="rounded border border-dashed p-4 text-center text-sm text-muted-foreground">
            Sin reviews. Se generan cuando el traveler vuelve tras el viaje.
          </p>
        ) : (
          <pre className="max-h-[200px] overflow-auto rounded bg-slate-50 p-2 text-[10px]">
            {JSON.stringify(rv, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function FollowUpTab({ lead }) {
  const qc = useQueryClient();
  const suggested = useQuery({
    queryKey: ["zicasso-suggested", lead.id],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/leads/${lead.id}/suggested-next-action`,
      { headers: authHeaders() })).data,
  });

  const snoozeMut = useMutation({
    mutationFn: (days) => axios.put(`${API}/api/zicasso/leads/${lead.id}/snooze`,
      { days }, { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Reminder snoozeado");
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
      qc.invalidateQueries({ queryKey: ["zicasso-leads"] });
    },
  });

  const dismissMut = useMutation({
    mutationFn: () => axios.put(`${API}/api/zicasso/leads/${lead.id}/dismiss-reminder`,
      {}, { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Reminder marcado como hecho");
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
      qc.invalidateQueries({ queryKey: ["zicasso-leads"] });
    },
  });

  const inactivateMut = useMutation({
    mutationFn: () => axios.put(`${API}/api/zicasso/leads/${lead.id}/status`,
      { status: "INACTIVE" }, { headers: authHeaders() }),
    onSuccess: () => {
      toast.success("Lead marcado como INACTIVE");
      qc.invalidateQueries({ queryKey: ["zicasso-lead", lead.id] });
      qc.invalidateQueries({ queryKey: ["zicasso-leads"] });
    },
  });

  const priorityColors = {
    high: "border-red-300 bg-red-50 text-red-900",
    medium: "border-amber-300 bg-amber-50 text-amber-900",
    low: "border-slate-200 bg-slate-50 text-slate-800",
  };

  const s = suggested.data;
  return (
    <div className="space-y-4" data-testid="followup-tab">
      {/* Estado actual del reminder */}
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4" /> Timeline Follow-up (Zicasso)
          </h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Step actual</div>
              <div className="text-lg font-bold">
                {lead.reminder_index !== null && lead.reminder_index !== undefined && lead.reminder_index >= 0
                  ? `${lead.reminder_index} / 9` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Próxima acción</div>
              <div className="text-sm">{lead.timeline_step_label || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Due</div>
              <div className={`text-sm font-medium ${lead.is_overdue ? "text-red-700" : ""}`}>
                {relativeDate(lead.reminder_due_at)}
                {lead.is_overdue && <span className="ml-1">(atrasado)</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sugerencia inteligente */}
      {suggested.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : s && (
        <Card className={`border-2 ${priorityColors[s.priority] || ""}`}>
          <CardContent className="p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles className="h-4 w-4" /> Sugerencia inteligente
              </h3>
              <Badge variant="outline" className={priorityColors[s.priority]}>
                Prioridad {s.priority}
              </Badge>
            </div>
            <p className="text-sm">{s.hint}</p>
            {s.suggested_template && (
              <div className="mt-3 rounded-md border bg-white p-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Template recomendado</div>
                <div className="text-sm font-semibold">{s.suggested_template.title}</div>
                <div className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">
                  {htmlToText(s.suggested_template.body).substring(0, 200)}...
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Acciones directas */}
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 text-sm font-semibold">Acciones</h3>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => dismissMut.mutate()}
              disabled={dismissMut.isPending}
              data-testid="dismiss-reminder-button"
            >
              <Check className="mr-1 h-3 w-3" /> Marcar hecho
            </Button>
            <Button size="sm" variant="outline" onClick={() => snoozeMut.mutate(1)} disabled={snoozeMut.isPending} data-testid="snooze-1d-button">
              <Clock className="mr-1 h-3 w-3" /> Snooze 1d
            </Button>
            <Button size="sm" variant="outline" onClick={() => snoozeMut.mutate(3)} disabled={snoozeMut.isPending} data-testid="snooze-3d-button">
              <Clock className="mr-1 h-3 w-3" /> Snooze 3d
            </Button>
            <Button size="sm" variant="outline" onClick={() => snoozeMut.mutate(7)} disabled={snoozeMut.isPending} data-testid="snooze-7d-button">
              <Clock className="mr-1 h-3 w-3" /> Snooze 7d
            </Button>
            <div className="w-full pt-2" />
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                if (window.confirm("¿Marcar este lead como INACTIVE en Zicasso?")) inactivateMut.mutate();
              }}
              disabled={inactivateMut.isPending}
              data-testid="mark-inactive-button"
            >
              <X className="mr-1 h-3 w-3" /> Marcar INACTIVE
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BestPracticesTab({ lead }) {
  const bp = useQuery({
    queryKey: ["zicasso-best-practices"],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/best-practices`,
      { headers: authHeaders() })).data,
    staleTime: 30 * 60_000,
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  if (bp.isLoading || !bp.data) return <Skeleton className="h-96 w-full" />;

  const currentStep = lead.reminder_index;
  const priorityColors = {
    high: "border-red-300 bg-red-50 text-red-900",
    medium: "border-amber-300 bg-amber-50 text-amber-900",
    low: "border-slate-200 bg-slate-50 text-slate-800",
  };

  return (
    <div className="space-y-4" data-testid="best-practices-tab">
      {/* Timeline oficial Zicasso */}
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <ListChecks className="h-4 w-4 text-primary" /> Timeline oficial de Zicasso
          </h3>
          <div className="space-y-2" data-testid="timeline-list">
            {bp.data.timeline.map((step) => {
              const isCurrent = currentStep === step.step;
              return (
                <div
                  key={step.step}
                  className={`flex gap-3 rounded-md border p-3 ${
                    isCurrent ? "border-primary bg-primary/5 ring-2 ring-primary" : ""
                  } ${priorityColors[step.priority]}`}
                  data-testid={`timeline-step-${step.step}`}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-sm font-bold shadow">
                    {step.step}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {step.day}
                      </span>
                      <Badge variant="outline" className="h-4 text-[9px]">
                        {step.channel === "call" ? "Llamada" : "Mensaje"}
                      </Badge>
                      <Badge variant="outline" className={`h-4 text-[9px] ${priorityColors[step.priority]}`}>
                        {step.priority}
                      </Badge>
                      {isCurrent && (
                        <Badge className="h-4 bg-primary text-[9px]">
                          ← Actual
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm font-semibold">{step.action}</div>
                    <div className="mt-1 text-[11px] leading-relaxed opacity-75">{step.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Reglas generales */}
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Lightbulb className="h-4 w-4 text-amber-500" /> Reglas de oro (conversión)
          </h3>
          <div className="space-y-2">
            {bp.data.rules.map((rule, i) => (
              <div key={i} className="rounded-md border-l-4 border-amber-400 bg-amber-50/30 p-3">
                <div className="text-sm font-semibold">{rule.title}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{rule.text}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Templates completos con preview HTML */}
      <Card>
        <CardContent className="p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <ScrollText className="h-4 w-4 text-primary" /> Templates disponibles ({bp.data.templates.length})
          </h3>
          <p className="mb-3 text-[11px] text-muted-foreground">
            Estos son los templates configurados en Zicasso. Haz click para verlos formateados.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {bp.data.templates.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTemplate(t)}
                className={`rounded-md border p-3 text-left text-xs hover:bg-slate-50 ${
                  selectedTemplate?.id === t.id ? "border-primary bg-primary/5" : ""
                }`}
                data-testid={`bp-template-${t.id}`}
              >
                <div className="font-semibold">{t.title}</div>
                <div className="mt-0.5 line-clamp-2 text-[10px] text-muted-foreground">
                  {htmlToText(t.body).substring(0, 100)}...
                </div>
              </button>
            ))}
          </div>

          {selectedTemplate && (
            <div className="mt-4 rounded-md border-2 border-primary/30 bg-slate-50 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-sm font-bold">{selectedTemplate.title}</h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedTemplate(null)}
                  data-testid="close-template-preview"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div
                className="prose prose-sm max-w-none text-xs leading-relaxed
                           [&_p]:my-1 [&_a]:text-blue-600 [&_a]:underline
                           [&_ul]:my-1 [&_ol]:my-1 [&_ul]:pl-4 [&_ol]:pl-4
                           [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedTemplate.body) }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fuente oficial */}
      <div className="rounded-md border border-dashed p-3 text-center">
        <p className="text-[11px] text-muted-foreground">
          Fuente: <a href={bp.data.sources[0]?.url} target="_blank" rel="noopener noreferrer"
                     className="text-primary underline">{bp.data.sources[0]?.label}</a>
        </p>
      </div>
    </div>
  );
}

export default function ZicassoLeadModal({ leadId, open, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["zicasso-lead", leadId],
    queryFn: async () => (await axios.get(`${API}/api/zicasso/leads/${leadId}`, { headers: authHeaders() })).data,
    enabled: !!leadId && open,
  });

  const lead = data;
  const stageColor = STAGE_COLORS[lead?.sales_stage || ""] || STAGE_COLORS[""];
  const stageLabel = STAGE_LABELS[lead?.sales_stage || ""] || "Nuevo";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-h-[92vh] w-[95vw] max-w-6xl overflow-hidden p-0"
        data-testid="zicasso-lead-modal"
      >
        <DialogTitle className="sr-only">Detalle del lead</DialogTitle>
        {isLoading || !lead ? (
          <div className="space-y-3 p-6">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="flex h-full max-h-[92vh] flex-col">
            {/* Header */}
            <div className="flex items-start justify-between border-b bg-gradient-to-r from-slate-50 to-white p-5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Avatar className="h-10 w-10 border-2 border-white shadow">
                    <AvatarFallback className="bg-slate-100 text-sm font-bold">
                      {initials(lead.traveler_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-xl font-bold" data-testid="modal-lead-name">
                        {lead.traveler_name}
                      </h2>
                      <Badge className={`border ${stageColor}`}>{stageLabel}</Badge>
                      {lead.is_overdue && (
                        <Badge variant="destructive">
                          <AlertTriangle className="mr-1 h-3 w-3" /> Atrasado
                        </Badge>
                      )}
                      {lead.repeat_traveler && (
                        <Badge variant="outline" className="border-purple-300 bg-purple-50 text-purple-700">
                          Repeat
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Ref: {lead.reference_id} · {(lead.countries || []).join(", ")} ·
                      asignado a <span className="font-medium">{lead.assigned_zc_agent_name}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="details" className="flex flex-1 flex-col overflow-hidden">
              <TabsList className="mx-5 mt-3 justify-start" data-testid="lead-tabs">
                <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                <TabsTrigger value="messages" data-testid="tab-messages">
                  <MessageSquare className="mr-1 h-3 w-3" />
                  Messages
                  {lead.messages_count > 0 && (
                    <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">
                      {lead.messages_count}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="documents" data-testid="tab-documents">
                  <FileText className="mr-1 h-3 w-3" />
                  Documents
                  {(lead.documents || []).length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">
                      {lead.documents.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger>
                <TabsTrigger value="followup" data-testid="tab-followup">
                  <ListChecks className="mr-1 h-3 w-3" />
                  Follow-up
                  {lead.is_overdue && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-red-500" />}
                </TabsTrigger>
                <TabsTrigger value="best-practices" data-testid="tab-best-practices">
                  <Lightbulb className="mr-1 h-3 w-3" />
                  Best Practices
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto p-5">
                <TabsContent value="details" className="mt-0">
                  <DetailsTab lead={lead} />
                </TabsContent>
                <TabsContent value="messages" className="mt-0">
                  <MessagesTab lead={lead} />
                </TabsContent>
                <TabsContent value="documents" className="mt-0">
                  <DocumentsTab lead={lead} />
                </TabsContent>
                <TabsContent value="sales" className="mt-0">
                  <SalesTab lead={lead} />
                </TabsContent>
                <TabsContent value="followup" className="mt-0">
                  <FollowUpTab lead={lead} />
                </TabsContent>
                <TabsContent value="best-practices" className="mt-0">
                  <BestPracticesTab lead={lead} />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
